/**
 * "Something's wrong here" — the only route a child has to say the app is wrong
 * rather than them.
 *
 * Across 541 lessons and thousands of clips there **will** be a recording that
 * plays the wrong word, a question with two right answers, a picture that does
 * not match. Telemetry finds the catastrophically broken ones, but it cannot
 * tell *"this is hard"* from *"the audio says the wrong word"* — only the child
 * can ([D-044](../../../docs/DECISIONS.md#d-044)).
 *
 * Without it a child who meets a broken question simply gets it wrong and learns
 * that the app is right and they are not. **That is the real harm, and it is
 * worse than the missing bug report.**
 *
 * The rules here are the ones that make it safe to tap. They are in `core`
 * rather than in a component because "reporting never costs anything" is a
 * property of the *system* — the client must not charge for it and the server
 * must not either — and a rule enforced in one place and forgotten in the other
 * is not a rule.
 */
/**
 * Why a child says something is wrong. Fixed choices, and no free-text box:
 * a child should not have to write in order to be heard, and it keeps typed
 * content out of the database entirely ([D-039](../../../docs/DECISIONS.md#d-039)).
 */
export const REPORT_REASONS = [
    'wrong_audio',
    'wrong_picture',
    'two_answers',
    'dont_understand',
    'other',
];
export const isReportReason = (value) => REPORT_REASONS.includes(value);
/**
 * How many reports one lesson may file.
 *
 * A child tapping it repeatedly for fun has to be harmless, and has to be
 * **silently** harmless: being told off for using the "something is wrong"
 * button teaches exactly the lesson this feature exists to prevent. Past the
 * limit the app still says thank you and still marks the answer correct; the row
 * is simply not written.
 *
 * Five is more than a real lesson of ten exercises would ever need and few
 * enough that a bored child cannot fill a table.
 */
export const REPORTS_PER_LESSON = 5;
/**
 * What happens when a child reports the exercise in front of them.
 *
 * `alreadyFiled` is how many this lesson has filed so far. The answer never
 * depends on the *reason*: every reason is equally free, including `other`.
 */
export const report = (alreadyFiled) => ({
    outcome: 'correct',
    score: 1,
    worthWriting: alreadyFiled < REPORTS_PER_LESSON,
});
//# sourceMappingURL=reports.js.map